import ReactDataSheet from 'react-datasheet';
import {
  CellDiv, CellLabel, StyledMatrixCell, StyledTable,
} from '../MatrixModel/styles';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Box, Button, ButtonVariant, Chart, IconName, Picker, Text, Tooltip,
} from '@usealloy/component-library';
import type { BulletChartProps } from '@usealloy/component-library';
import AxisHeaderInput from '../MatrixModel/AxisHeaderInput';
import {
  AXIS_HIGH_UNBOUNDED_NUMBER,
  AXIS_LOW_UNBOUNDED_NUMBER,
  getCellLabel,
  HighRangeOption,
  LowRangeOption,
  MatrixAxisOperation,
} from 'flow/service/actions';
import { createNewValueRangeConfig, getRangePadding, getUpdatedAxis } from './helpers';
import type { GridElement } from '../MatrixModel/MatrixModel';
import type { OnAxisLengthChangeProps, OnAxisValueChangeProps } from '../MatrixModel/AxisHeaderInput/types';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import { ValueRangeConfigPopover } from './ValueRangeConfigPopover';

interface ValueRangeConfigProps {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
  updateOutputValueRange: (outputValueRange: OutputValueRange[]) => void;
  updateOutputValueRangeOptions: (outputValueRangeOptions: ValueRangeConfigOptions) => void;
}

export const ValueRangeConfig = ({
  outputValueRange,
  outputValueRangeOptions,
  updateOutputValueRange,
  updateOutputValueRangeOptions,
}: ValueRangeConfigProps) => {
  const convertedAxisHeader = useMemo(
    () => {
      const axisHeader = [...outputValueRange.map(({ minValue }) => minValue)];
      if (outputValueRangeOptions?.lowRange === LowRangeOption.unbounded) {
        axisHeader[0] = AXIS_LOW_UNBOUNDED_NUMBER;
      }
      if (outputValueRangeOptions?.highRange === HighRangeOption.unbounded) {
        axisHeader.push(AXIS_HIGH_UNBOUNDED_NUMBER);
      } else if (
        outputValueRangeOptions?.highRange === HighRangeOption.bounded
        && outputValueRange.length > 0
      ) {
        axisHeader.push(outputValueRange[outputValueRange.length - 1].maxValue);
      }
      return axisHeader;
    },
    [outputValueRangeOptions, outputValueRange],
  );

  const initializeEmptyConfig = useCallback(() => {
    const newValueRangeConfig = [createNewValueRangeConfig({ id: '1' })];
    updateOutputValueRangeOptions({
      lowRange: LowRangeOption.bounded,
      highRange: HighRangeOption.bounded,
    });
    updateOutputValueRange(newValueRangeConfig);
  }, [updateOutputValueRange, updateOutputValueRangeOptions]);

  useEffect(() => {
    if (!outputValueRange?.length) {
      initializeEmptyConfig();
    }
  }, [initializeEmptyConfig, outputValueRange.length]);

  const onAxisValueChange = useCallback(({
    index, value,
  }: OnAxisValueChangeProps) => {
    const updatedValue = {
      ...outputValueRange[index],
      minValue: value as number,
    };
    const updatedPreviousValue = {
      ...outputValueRange[index - 1],
      maxValue: value as number,
    };

    const updatedAxis = getUpdatedAxis({
      index,
      outputValueRange,
      updatedPreviousValue,
      updatedValue,
    });
    updateOutputValueRange(updatedAxis);
  }, [outputValueRange, updateOutputValueRange]);

  const onAxisLengthChange = useCallback(({
    op,
  }: OnAxisLengthChangeProps) => {
    const updatedOutputValueRange = JSON.parse(JSON.stringify(outputValueRange));
    if (op === MatrixAxisOperation.DELETE) {
      updatedOutputValueRange.pop();
      if (outputValueRangeOptions.highRange === HighRangeOption.unbounded) {
        updatedOutputValueRange[updatedOutputValueRange.length - 1].maxValue = null;
      }
    } else if (op === MatrixAxisOperation.ADD) {
      updatedOutputValueRange.push(createNewValueRangeConfig({
        id: `${Number(outputValueRange[outputValueRange.length - 1].id) + 1}`,
        min: outputValueRange[outputValueRange.length - 1].maxValue,
      }));
    }
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, outputValueRangeOptions?.highRange, updateOutputValueRange]);

  const getAxisHeaders = useCallback(() => {
    const convertedAxisSettings = {
      axisHeader: convertedAxisHeader,
      axisHeaderDataType: 'number',
      order: 'asc',
    };
    const axisValuesToMap = convertedAxisHeader.length === 1
      ? convertedAxisHeader
      // each range is handled with index and index + 1
      : convertedAxisHeader.slice(0, -1);

    const axisHeaders: any[] = [];

    axisValuesToMap.forEach((value: number, index: number) => {
      axisHeaders.push({
        value,
        valueViewer: () => (
          <span
            data-dd-action-name={`value-range-config-axis-header-y-${index + 1}`}
          >
            <AxisHeaderInput
              axis="Y"
              axisSettings={convertedAxisSettings}
              index={index}
              onChange={onAxisValueChange}
              onDelete={() => onAxisLengthChange({
                axis: 'Y', op: MatrixAxisOperation.DELETE,
              })}
            />
          </span>
        ),
        disableEvents: true,
      });
    });
    return axisHeaders;
  }, [convertedAxisHeader, onAxisLengthChange, onAxisValueChange]);

  const onLabelChange = useCallback((
    [{ value, row }]: ReactDataSheet.CellsChangedArgs<GridElement, string>,
  ) => {
    const index = row - 1;
    const updatedOutputValueRange = [
      ...outputValueRange.toSpliced(
        index,
        1,
        {
          ...outputValueRange[index],
          label: value,
        },
      ),
    ];
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, updateOutputValueRange]);

  const onColorChange = useCallback(({ color, index }: { color: string; index: number }) => {
    const updatedOutputValueRange = [
      ...outputValueRange.toSpliced(
        index,
        1,
        {
          ...outputValueRange[index],
          color,
        },
      ),
    ];
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, updateOutputValueRange]);

  const matrixGridToRender = useMemo(() => {
    const headerCol = getAxisHeaders();

    const matrixGrid = [[]];
    return headerCol.reduce((acc, curr, index) => {
      const arr = [];
      const cellLabelValue = getCellLabel(0, index);
      const label = outputValueRange[index]?.label;
      const color = outputValueRange[index]?.color;
      // add the header cell
      arr.push(curr);
      // add the value cell
      arr.push({
        value: label,
        readOnly: false,
        valueViewer: ({ value: cellValue }) => (
          <Box data-dd-action-name={`value-range-config-cell-${label}`}>
            <CellLabel>{cellLabelValue}</CellLabel>
            <CellDiv>{cellValue}</CellDiv>
          </Box>
        ),
      });
      // add the color picker cell
      arr.push({
        value: label,
        readOnly: true,
        valueViewer: () => (
          <Box data-dd-action-name={`value-range-config-cell-${label}`}>
            <Picker
              style={{ border: 'none' }}
              variant="color"
              value={color}
              onChange={newColor => {
                onColorChange({ color: newColor, index });
              }}
            />
          </Box>
        ),
      });
      acc.push(arr);
      return acc;
    }, matrixGrid);
  }, [getAxisHeaders, onColorChange, outputValueRange]);

  const { colorRanges, animatedRangeIndices, hasAtLeastOneColor } = useMemo(() => {
    const rangesWithColors = outputValueRange.filter(
      ({ color }) => Boolean(color && color.trim()),
    );
    const hasColor = rangesWithColors.length > 0;

    // Calculate rangePadding from ranges that have both min and max values
    const rangesWithValues = outputValueRange.filter(
      ({ minValue, maxValue }) => minValue !== null && maxValue !== null,
    );
    let calculatedPadding: number;
    if (rangesWithValues.length === 0) {
      calculatedPadding = 1;
    } else if (rangesWithValues.length === 1) {
      calculatedPadding = rangesWithValues[0].maxValue - rangesWithValues[0].minValue;
    } else {
      calculatedPadding = getRangePadding(outputValueRange);
    }
    const rangePadding = Number.isFinite(calculatedPadding) && calculatedPadding > 0
      ? calculatedPadding
      : 1;

    const ranges: BulletChartProps['colorRanges'] = [];

    const pendingIndices = [];
    let currentEndPosition = null;

    outputValueRange.forEach(({
      color, maxValue, minValue, label,
    }, index) => {
      const rangeHasColor = Boolean(color && color.trim());
      const isFirstRow = index === 0;
      const isLastRow = index === outputValueRange.length - 1;
      const isFirstUnbounded = isFirstRow
        && outputValueRangeOptions?.lowRange === LowRangeOption.unbounded;
      const isLastUnbounded = isLastRow
        && outputValueRangeOptions?.highRange === HighRangeOption.unbounded;

      // A range is valid (will have a label) if it has a color and the required values
      // First row unbounded: needs (min OR max) and color
      // Last row unbounded: needs min and color
      // Last row bounded: needs min AND max and color
      // Middle rows: needs min and color (maxValue can be null if it was previously valid)
      let isValid = false;
      if (rangeHasColor) {
        if (isFirstUnbounded) {
          isValid = minValue !== null || maxValue !== null;
        } else if (isLastUnbounded) {
          isValid = minValue !== null;
        } else if (isLastRow) {
          isValid = minValue !== null && maxValue !== null;
        } else {
          isValid = minValue !== null;
        }
      }

      if (isValid) {
        // Range has a color - use its actual values
        let from = minValue ?? (
          maxValue !== null ? maxValue - rangePadding : currentEndPosition ?? 0
        );
        let to = maxValue ?? (
          minValue !== null ? minValue + rangePadding : from + rangePadding
        );

        // Ensure valid range
        if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
          from = minValue ?? maxValue ?? currentEndPosition ?? 0;
          to = maxValue ?? from + rangePadding;
        }
        if (from >= to) {
          to = from + rangePadding;
        }

        // Format label - valid ranges show labels
        const minLabel = isFirstUnbounded
          ? '-∞'
          : minValue ?? '-∞';

        let maxLabel = null;
        if (isLastUnbounded) {
          maxLabel = '∞';
        } else if (isLastRow) {
          maxLabel = maxValue !== null ? String(maxValue) : '∞';
        } else {
          maxLabel = maxValue !== null ? String(maxValue) : null;
        }

        const formattedLabel = `${label || ''} ${minLabel} - ${maxLabel ?? ''}`.trim() || '';

        ranges.push({
          color: `#${color}`,
          label: formattedLabel,
          from,
          to,
        });
        currentEndPosition = currentEndPosition ? Math.max(currentEndPosition, to) : to;
      } else {
        // Pending range (incomplete or no color) - position sequentially after valid ranges
        const previousMaxValue = index > 0
          ? (outputValueRange[index - 1].maxValue ?? outputValueRange[index - 1].minValue)
          : null;

        let from = minValue ?? currentEndPosition ?? 0;
        if (minValue !== null) {
          from = Math.max(from, currentEndPosition ?? 0);
        }

        let to = maxValue ?? (previousMaxValue ?? from) + rangePadding;
        if (from >= to) {
          to = from + rangePadding;
        }

        // Pending ranges (blinking) don't show labels
        // Use selected color if present, otherwise gray
        ranges.push({
          color: rangeHasColor ? `#${color}` : '#808080',
          from,
          to,
          label: '',
        });

        // Mark as pending (except first row)
        if (index > 0) {
          pendingIndices.push(ranges.length - 1);
        }

        currentEndPosition = to;
      }
    });


    return {
      colorRanges: ranges,
      animatedRangeIndices: pendingIndices,
      hasAtLeastOneColor: hasColor,
    };
  }, [outputValueRange, outputValueRangeOptions]);

  return (
    <Box
      direction="column"
      gap={0}
      justifyContent="flex-start"
      style={{ marginLeft: 'calc(-1 * var(--cl-space-3)' }}
    >
      <Box justifyContent="flex-start" style={{ paddingLeft: 'var(--cl-space-3)' }}>
        <Text>Configure Value Ranges</Text>
        <ValueRangeConfigPopover
          outputValueRange={outputValueRange}
          outputValueRangeOptions={outputValueRangeOptions}
          updateOutputValueRange={updateOutputValueRange}
          updateOutputValueRangeOptions={updateOutputValueRangeOptions}
        />
      </Box>
      {outputValueRange.length > 0 && hasAtLeastOneColor && (
        <div
          style={{
            minWidth: '100%',
            marginTop: 'calc(-1 * var(--cl-space-7))',
            zIndex: -1,
          }}
        >
          <Chart.Bullet
            colorRanges={colorRanges}
            animatedRangeIndices={animatedRangeIndices}
          />
        </div>
      )}
      <Box direction="column" alignItems="flex-start" justifyContent="flex-start">
        <StyledTable style={{ padding: '0 var(--cl-space-3)', width: '100%' }}>
          <ReactDataSheet
            data={matrixGridToRender}
            cellRenderer={({
              children, row, col, ...rest
            }) => (
              <StyledMatrixCell {...rest}>
                {children}
              </StyledMatrixCell>
            )}
            onCellsChanged={onLabelChange}
            valueRenderer={(cell: GridElement) => cell.value}
          />
        </StyledTable>
        <Tooltip content="Add Row">
          <Box
            justifyContent="flex-start"
            width="auto"
            data-dd-action-name="value-range-config-add-new-row"
          >
            <Button
              icon={IconName.Add}
              variant={ButtonVariant.tertiary}
              onClick={() => onAxisLengthChange({
                axis: 'Y', op: MatrixAxisOperation.ADD,
              })}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};
